import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useConceptGate } from '../../../../../context/ConceptGateContext';
import { Direction } from '../../../../../api/conceptgate';

/**
 * Hook to create trades by clicking on the chart.
 *
 * Flow:
 * 1. User activates "create trade" mode
 * 2. First click sets entry price
 * 3. Second click sets stop price (determines direction: stop > entry = SHORT, stop < entry = LONG)
 * 4. Third click sets target price
 * 5. Trade is created in ConceptGateContext
 *
 * Visual feedback:
 * - Cursor changes to crosshair when in creation mode
 * - Preview lines show as user clicks
 * - Toast/status shows current step
 */

// Creation steps
const STEPS = {
  IDLE: 'idle',
  ENTRY: 'entry',      // Waiting for entry click
  STOP: 'stop',        // Waiting for stop click
  TARGET: 'target',    // Waiting for target click
  COMPLETE: 'complete' // All prices set
};

export const useChartTradeCreation = (chartRef, candleSeriesRef, chartReady = true, chartInstanceId = 0) => {
  const { setTradeSpec, selectedInstrument, clearTrade } = useConceptGate();

  // Creation mode state
  const [isCreating, setIsCreating] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.IDLE);

  // Temporary prices during creation
  const [pendingTrade, setPendingTrade] = useState({
    entryPrice: null,
    stopPrice: null,
    targetPrice: null,
  });

  // Store preview lines for cleanup
  const previewLinesRef = useRef({
    entry: null,
    stop: null,
    target: null,
  });

  // Ref to always get the latest handler (avoids stale closure in subscriptions)
  const handleChartClickRef = useRef(null);

  // Line styles for preview (memoized)
  const previewStyles = useMemo(() => ({
    entry: { color: '#2196F3', lineWidth: 1, lineStyle: 1, title: 'Entry (preview)' },
    stop: { color: '#EF4444', lineWidth: 1, lineStyle: 1, title: 'Stop (preview)' },
    target: { color: '#22C55E', lineWidth: 1, lineStyle: 1, title: 'Target (preview)' },
  }), []);

  // Clear all preview lines
  const clearPreviewLines = useCallback(() => {
    const series = candleSeriesRef?.current;
    if (!series) return;

    Object.entries(previewLinesRef.current).forEach(([key, line]) => {
      if (line) {
        try {
          series.removePriceLine(line);
        } catch (e) {
          // Line may already be removed
        }
        previewLinesRef.current[key] = null;
      }
    });
  }, [candleSeriesRef]);

  // Create a preview line
  const createPreviewLine = useCallback((type, price) => {
    const series = candleSeriesRef?.current;
    if (!series || !price) return;

    // Remove existing preview line of this type
    if (previewLinesRef.current[type]) {
      try {
        series.removePriceLine(previewLinesRef.current[type]);
      } catch (e) {
        // Ignore
      }
    }

    // Create new preview line
    const line = series.createPriceLine({
      price: parseFloat(price),
      ...previewStyles[type],
      axisLabelVisible: true,
    });

    previewLinesRef.current[type] = line;
  }, [candleSeriesRef, previewStyles]);

  // Start trade creation mode
  const startCreation = useCallback(() => {
    // Clear any existing trade first
    clearTrade();
    clearPreviewLines();

    setPendingTrade({
      entryPrice: null,
      stopPrice: null,
      targetPrice: null,
    });

    setCurrentStep(STEPS.ENTRY);
    setIsCreating(true);
  }, [clearTrade, clearPreviewLines]);

  // Cancel creation mode
  const cancelCreation = useCallback(() => {
    clearPreviewLines();
    setPendingTrade({
      entryPrice: null,
      stopPrice: null,
      targetPrice: null,
    });
    setCurrentStep(STEPS.IDLE);
    setIsCreating(false);
  }, [clearPreviewLines]);

  // Handle chart click - this callback updates frequently, so we store it in a ref
  const handleChartClick = useCallback((param) => {
    console.log('[TradeCreation] Chart clicked!', param);

    if (!candleSeriesRef?.current) {
      console.log('[TradeCreation] No candle series ref');
      return;
    }

    if (!param?.point?.y) {
      console.log('[TradeCreation] No click point', param);
      return;
    }

    // Get price from click position
    const series = candleSeriesRef.current;
    const price = series.coordinateToPrice(param.point.y);

    console.log('[TradeCreation] Click Y:', param.point.y, 'Price:', price, 'Step:', currentStep);

    if (price === null || price === undefined) {
      console.log('[TradeCreation] Could not convert coordinate to price');
      return;
    }

    // Round to appropriate precision (5 decimal places for forex)
    const roundedPrice = Math.round(price * 100000) / 100000;

    switch (currentStep) {
      case STEPS.ENTRY:
        console.log('[TradeCreation] Setting entry price:', roundedPrice);
        setPendingTrade(prev => ({ ...prev, entryPrice: roundedPrice }));
        createPreviewLine('entry', roundedPrice);
        setCurrentStep(STEPS.STOP);
        break;

      case STEPS.STOP:
        console.log('[TradeCreation] Setting stop price:', roundedPrice);
        setPendingTrade(prev => ({ ...prev, stopPrice: roundedPrice }));
        createPreviewLine('stop', roundedPrice);
        setCurrentStep(STEPS.TARGET);
        break;

      case STEPS.TARGET:
        console.log('[TradeCreation] Setting target price:', roundedPrice);
        const finalTrade = {
          ...pendingTrade,
          targetPrice: roundedPrice,
        };

        createPreviewLine('target', roundedPrice);

        // Determine direction based on entry vs stop
        const direction = finalTrade.stopPrice < finalTrade.entryPrice
          ? Direction.LONG
          : Direction.SHORT;

        // Create the trade in context
        setTradeSpec({
          instrument: selectedInstrument || 'EUR_USD',
          direction,
          entryPrice: finalTrade.entryPrice,
          stopPrice: finalTrade.stopPrice,
          targetPrice: finalTrade.targetPrice,
        });

        // Clear preview lines (real lines will be shown by useTradePriceLines)
        setTimeout(() => {
          clearPreviewLines();
        }, 100);

        // Complete creation
        setCurrentStep(STEPS.COMPLETE);
        setIsCreating(false);
        setPendingTrade({
          entryPrice: null,
          stopPrice: null,
          targetPrice: null,
        });
        break;

      default:
        console.log('[TradeCreation] Unknown step:', currentStep);
        break;
    }
  }, [currentStep, candleSeriesRef, pendingTrade, createPreviewLine, clearPreviewLines, setTradeSpec, selectedInstrument]);

  // Keep the ref updated with the latest handler
  useEffect(() => {
    handleChartClickRef.current = handleChartClick;
  }, [handleChartClick]);

  // Stable wrapper function that always calls the latest handler via ref
  // This avoids stale closure issues when subscribing to chart events
  const stableClickHandler = useCallback((param) => {
    if (handleChartClickRef.current) {
      handleChartClickRef.current(param);
    }
  }, []);

  // Subscribe to chart clicks when in creation mode
  useEffect(() => {
    if (!chartReady || !isCreating) {
      console.log('[TradeCreation] Not ready to subscribe - chartReady:', chartReady, 'isCreating:', isCreating);
      return;
    }

    const chart = chartRef?.current;
    if (!chart) {
      console.log('[TradeCreation] Chart ref not available, will retry...');
      // Chart might not be ready yet, poll for it
      let retryCount = 0;
      const maxRetries = 50; // 5 seconds max

      const checkInterval = setInterval(() => {
        retryCount++;
        const chartNow = chartRef?.current;

        if (chartNow) {
          console.log('[TradeCreation] Chart now available after', retryCount * 100, 'ms, subscribing');
          chartNow.subscribeClick(stableClickHandler);
          clearInterval(checkInterval);
        } else if (retryCount >= maxRetries) {
          console.log('[TradeCreation] Max retries reached, chart still not available');
          clearInterval(checkInterval);
        }
      }, 100);

      // Capture the chart reference now for cleanup
      const chartAtStart = chartRef?.current;
      return () => {
        clearInterval(checkInterval);
        if (chartAtStart) {
          try {
            chartAtStart.unsubscribeClick(stableClickHandler);
          } catch (e) {
            // Chart may be disposed
          }
        }
      };
    }

    console.log('[TradeCreation] Subscribing to chart clicks immediately');
    chart.subscribeClick(stableClickHandler);

    return () => {
      console.log('[TradeCreation] Unsubscribing from chart clicks');
      try {
        chart.unsubscribeClick(stableClickHandler);
      } catch (e) {
        // Chart may be disposed
      }
    };
  }, [chartRef, chartReady, isCreating, stableClickHandler, chartInstanceId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPreviewLines();
    };
  }, [clearPreviewLines]);

  // Restore preview lines after chart re-init (e.g., timeframe change)
  useEffect(() => {
    if (isCreating && chartReady && candleSeriesRef?.current) {
      // Small delay to ensure chart is fully initialized
      const timer = setTimeout(() => {
        if (pendingTrade.entryPrice) createPreviewLine('entry', pendingTrade.entryPrice);
        if (pendingTrade.stopPrice) createPreviewLine('stop', pendingTrade.stopPrice);
        if (pendingTrade.targetPrice) createPreviewLine('target', pendingTrade.targetPrice);
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartInstanceId, isCreating, chartReady, pendingTrade, createPreviewLine]);

  // Get step instruction text
  const getStepInstruction = useCallback(() => {
    switch (currentStep) {
      case STEPS.ENTRY:
        return 'Click to set entry price';
      case STEPS.STOP:
        return 'Click to set stop loss';
      case STEPS.TARGET:
        return 'Click to set target price';
      case STEPS.COMPLETE:
        return 'Trade created!';
      default:
        return '';
    }
  }, [currentStep]);

  return {
    // State
    isCreating,
    currentStep,
    pendingTrade,

    // Actions
    startCreation,
    cancelCreation,

    // Helpers
    getStepInstruction,

    // Constants for external use
    STEPS,
  };
};

export default useChartTradeCreation;
